import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";
import { SEOHead } from "@/components/SEOHead";

interface Cookie {
  name: string;
  purpose: string;
  type: "Essential" | "Analytics" | "Preference" | "Marketing";
  duration: string;
  provider: string;
}

export default function Cookies() {
  const [cookies, setCookies] = useState<Cookie[]>([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPolicy();
    scanCookies();
    const interval = setInterval(scanCookies, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchPolicy = async () => {
    const { data } = await supabase
      .from("site_policies")
      .select("*")
      .eq("policy_type", "cookies")
      .single();

    if (data) {
      setContent(data.content);
    }
    setLoading(false);
  };

  const scanCookies = () => {
    const detectedCookies: Cookie[] = [
      {
        name: "cookie-consent",
        purpose: "Stores your cookie consent preferences",
        type: "Essential",
        duration: "1 year",
        provider: "PTA Hub"
      },
      {
        name: "session",
        purpose: "Maintains your login session",
        type: "Essential",
        duration: "Session",
        provider: "PTA Hub"
      },
      {
        name: "theme-preference",
        purpose: "Remembers your dark/light mode preference",
        type: "Preference",
        duration: "1 year",
        provider: "PTA Hub"
      }
    ];

    const browserCookies = document.cookie.split(';');
    browserCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName && !detectedCookies.find(c => c.name === cookieName)) {
        detectedCookies.push({
          name: cookieName,
          purpose: "Third-party cookie",
          type: "Analytics",
          duration: "Varies",
          provider: "Third Party"
        });
      }
    });

    setCookies(detectedCookies);
  };

  const getTypeBadgeVariant = (type: Cookie["type"]) => {
    switch (type) {
      case "Essential":
        return "default";
      case "Analytics":
        return "secondary";
      case "Preference":
        return "outline";
      case "Marketing":
        return "destructive";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl mx-auto">
      <SEOHead slug="cookies" />
      <div>
        <h1 className="text-3xl font-bold text-foreground">Cookie Policy</h1>
        <p className="text-muted-foreground mt-2">Last updated: {new Date().toLocaleDateString()}</p>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>What Are Cookies?</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-slate dark:prose-invert max-w-none">
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, {
            ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'blockquote', 'div', 'span'],
            ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style']
          }) }} />
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Cookies We Use</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            This table shows all cookies currently active on our platform. It updates automatically as cookies are added or removed.
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cookie Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Provider</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cookies.map((cookie, index) => (
                <TableRow key={index}>
                  <TableCell className="font-mono text-xs">{cookie.name}</TableCell>
                  <TableCell>
                    <Badge variant={getTypeBadgeVariant(cookie.type)}>
                      {cookie.type}
                    </Badge>
                  </TableCell>
                  <TableCell>{cookie.purpose}</TableCell>
                  <TableCell className="text-sm">{cookie.duration}</TableCell>
                  <TableCell className="text-sm">{cookie.provider}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
